import { useEffect, useState } from "react";
import { useAba } from "@/lib/useAba";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Bike } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, isManagerRole } from "@/lib/api";
import { toast } from "sonner";
import { LinksQrTab } from "@/components/menu/LinksQrTab";
import { copyText } from "@/lib/qr";
import { LogoField, BannersField } from "@/components/menu/BrandingFields";
import { toBanner } from "@/components/menu/BannerSchedule";

const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function MenuSettings() {
  const aba = useAba("visual");
  const { session, refresh } = useAuth();
  const t = session.tenant;
  const manager = isManagerRole(session.user);
  const base = `${window.location.origin}/cardapio/${t.menu_slug}`;
  const [s, setS] = useState({ ...t.settings, banners: (t.settings.banners?.length ? t.settings.banners : t.settings.banner_url ? [t.settings.banner_url] : []).map(toBanner) });
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); api.get("/tables").then((r) => setTables(r.data)).catch(() => setTables([])); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const keys = ["menu_enabled", "logo_url", "banners", "brand_color", "opening_hours"];
      const settings = { ...Object.fromEntries(keys.map((k) => [k, s[k]])), banner_url: s.banners[0]?.url || "" };
      await api.put("/settings", { razao_social: t.razao_social, nome_fantasia: t.nome_fantasia, telefone: t.telefone, endereco: t.endereco, segmento: t.segmento, settings });
      await refresh();
      toast.success("Cardápio salvo");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (p, k) => {
    try {
      const { id, tenant_id, created_at, ...body } = p;
      const value = !(p[k] ?? (k === "available"));
      const tm = session.user.trial_modules;
      const { data } = tm && !tm.includes("produtos") ? await api.patch(`/cardapio/products/${id}`, { [k]: value })
        : await api.put(`/products/${id}`, { ...body, [k]: value });
      setProducts((l) => l.map((x) => (x.id === id ? data : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const hours = (d, patch) => set("opening_hours", { ...s.opening_hours, [d]: { ...s.opening_hours[d], ...patch } });

  return (
    <>
      <PageHeader eyebrow="Pedidos online" title="Cardápio digital" subtitle="Identidade visual, horários, produtos, links e QR Codes."
        actions={<>
          <Button variant="outline" onClick={async () => { await copyText(base); toast.success("Link copiado"); }} data-testid="copy-menu-url-btn"><Copy className="h-4 w-4 mr-1" />Copiar link</Button>
          <Button asChild variant="outline" data-testid="open-menu-btn"><a href={base} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1" />Abrir</a></Button>
          {manager && <Button onClick={save} data-testid="save-menu-settings-btn">Salvar</Button>}
        </>} />
      <Input readOnly value={base} className="font-mono text-sm" data-testid="menu-public-url" />
      <Tabs key={aba} defaultValue={aba}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="visual" data-testid="menu-tab-visual">Identidade</TabsTrigger>
          <TabsTrigger value="delivery" data-testid="menu-tab-delivery">Horários</TabsTrigger>
          <TabsTrigger value="produtos" data-testid="menu-tab-produtos">Produtos</TabsTrigger>
          <TabsTrigger value="qr" data-testid="menu-tab-qr">Links e QR Codes</TabsTrigger>
        </TabsList>
        <TabsContent value="visual" className="mt-4">
          <div className="card-surface p-5 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <LogoField value={s.logo_url} onChange={(v) => set("logo_url", v)} />
            <div className="space-y-4">
            <Field label="Cor da marca"><div className="flex gap-2"><input type="color" value={s.brand_color} onChange={(e) => set("brand_color", e.target.value)} className="h-10 w-14 rounded border" data-testid="menu-color-input" /><Input value={s.brand_color} onChange={(e) => set("brand_color", e.target.value)} /></div></Field>
            <label className="flex h-10 items-center justify-between rounded-lg border px-4 text-sm">Receber pedidos pelo cardápio<Switch checked={s.menu_enabled} onCheckedChange={(v) => set("menu_enabled", v)} data-testid="menu-enabled-switch" /></label>
            </div>
          </div>
          <BannersField value={s.banners} onChange={(v) => set("banners", v)} />
          {manager && <p className="text-xs text-muted-foreground">Depois de enviar ou trocar as imagens, clique em <b>Salvar</b> no topo da página para publicar.</p>}
          </div>
        </TabsContent>
        <TabsContent value="delivery" className="mt-4 space-y-4">
          <div className="card-surface p-5 flex flex-wrap items-center justify-between gap-3" data-testid="delivery-moved-note">
            <p className="text-sm"><b>Área de entrega, taxas, pedido mínimo, prazos e rota do entregador</b> ficam no menu Delivery, na aba "Área de entrega e taxas".</p>
            <Button asChild variant="outline" size="sm"><Link to="/app/delivery?aba=area" data-testid="go-delivery-area-btn"><Bike className="h-4 w-4 mr-1" />Abrir área de entrega</Link></Button>
          </div>
          <div className="card-surface p-5 space-y-2" data-testid="opening-hours">
            <p className="eyebrow">Horário de funcionamento</p>
            {DAYS.map((d, i) => (
              <div key={d} className="flex items-center gap-3 text-sm">
                <Switch checked={s.opening_hours[i]?.enabled} onCheckedChange={(v) => hours(i, { enabled: v })} data-testid={`hours-enabled-${i}`} />
                <span className="w-20">{d}</span>
                <Input type="time" value={s.opening_hours[i]?.open} onChange={(e) => hours(i, { open: e.target.value })} className="w-32" />
                <span>às</span>
                <Input type="time" value={s.opening_hours[i]?.close} onChange={(e) => hours(i, { close: e.target.value })} className="w-32" />
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="produtos" className="mt-4">
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm" data-testid="menu-products-list">
              <thead><tr className="text-left text-muted-foreground"><th className="p-3">Produto</th><th>Preço</th><th>No cardápio</th><th>Destaque</th><th>Disponível</th></tr></thead>
              <tbody>{products.map((p) => (
                <tr key={p.id} className="border-t"><td className="p-3">{p.name}{p.option_groups?.length > 0 && <span className="ml-1 text-xs text-muted-foreground">({p.option_groups.length} grupos de opções)</span>}</td><td className="font-mono">{brl(p.price)}</td>
                  <td><Switch checked={p.show_in_menu} disabled={!manager} onCheckedChange={() => toggle(p, "show_in_menu")} data-testid={`menu-product-switch-${p.id}`} /></td>
                  <td><Switch checked={!!p.featured} disabled={!manager} onCheckedChange={() => toggle(p, "featured")} data-testid={`menu-featured-switch-${p.id}`} /></td>
                  <td><Switch checked={p.available !== false} disabled={!manager} onCheckedChange={() => toggle(p, "available")} data-testid={`menu-available-switch-${p.id}`} /></td></tr>
              ))}</tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Adicionais, complementos, tamanhos e sabores são configurados no cadastro de cada produto (Produtos → editar).</p>
        </TabsContent>
        <TabsContent value="qr" className="mt-4">
          <LinksQrTab base={base} tenant={t} settings={s} tables={tables} />
        </TabsContent>
      </Tabs>
    </>
  );
}
